// IbanEingabe — nur das <input> einer IBAN, ohne Label und ohne eigenes
// Layout. Absicht: Die drei Masken, die eine IBAN erfassen (Kontaktakte im
// Refined-Look, „Bankverbindungen verwalten"-Dialog mit eigenen Inline-Styles,
// Kontakt-Anlage mit den forms/widgets), bringen ihr Drumherum selbst mit und
// teilen sich hier ausschließlich das Verhalten:
//
//   • gruppiert schon beim Tippen in 4er-Blöcke (DE42 3905 0000 1073 3002 69)
//   • hält dabei den Cursor an der Stelle, an der er fachlich stehen muss
//   • meldet die aufgelöste Bank (BIC + Name), sobald die IBAN vollständig ist
//
// Der Cursor ist der Grund für die Ref: Ein kontrolliertes Input setzt den
// Cursor nach einem Wert-Wechsel ans Ende. Wer mitten in einer bestehenden
// IBAN eine Ziffer einfügt, würde also nach jedem Zeichen ans Ende springen.
// Deshalb merken wir uns die Zielposition beim Tippen und setzen sie im
// useLayoutEffect — nach dem Commit des neuen Werts, aber vor dem Paint, damit
// kein Springen sichtbar wird.

import { useLayoutEffect, useRef, type CSSProperties } from "react";
import { ibanTippen } from "../validation/iban";
import { useBankAusIban } from "./useBankAusIban";
import type { BankTreffer } from "./bankleitzahlen";
import { aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

export function IbanEingabe({
  value,
  onChange,
  onBankErkannt,
  className,
  style,
  placeholder,
  readOnly,
  onFocus,
  onBlur,
  id,
  name,
  "aria-invalid": ariaInvalid,
}: {
  value?: string;
  onChange?: (v: string) => void;
  /**
   * Wird gemeldet, sobald aus der IBAN eine Bank ableitbar ist (nur DE).
   * Der Aufrufer entscheidet, ob er BIC/Bankname übernimmt — ein bereits
   * ausgefülltes Feld soll er nicht überschreiben.
   */
  onBankErkannt?: (bank: BankTreffer) => void;
  className?: string;
  /** Inline-Styles für Masken, die ihr Aussehen nicht über Klassen setzen. */
  style?: CSSProperties;
  placeholder?: string;
  readOnly?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  id?: string;
  name?: string;
  "aria-invalid"?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const zielCursor = useRef<number | null>(null);

  useLayoutEffect(() => {
    const ziel = zielCursor.current;
    zielCursor.current = null;
    const el = ref.current;
    if (ziel == null || !el || aktivesDokument().activeElement !== el) return;
    el.setSelectionRange(ziel, ziel);
  }, [value]);

  // Die Bank wird beim TIPPEN geprüft, nicht über den Wert: Sonst trüge die
  // Maske BIC und Institut schon beim Öffnen einer Akte nach und die
  // Speicherleiste ginge ungefragt auf (siehe useBankAusIban).
  const bankPruefen = useBankAusIban(onBankErkannt);

  return (
    <input
      ref={ref}
      id={id}
      name={name}
      className={className}
      style={style}
      value={value ?? ""}
      placeholder={placeholder}
      readOnly={readOnly}
      inputMode="text"
      autoComplete="off"
      spellCheck={false}
      aria-invalid={ariaInvalid}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={
        onChange
          ? (e) => {
              const el = e.target;
              const ergebnis = ibanTippen(
                String(value ?? ""),
                el.value,
                el.selectionStart ?? el.value.length,
              );
              zielCursor.current = ergebnis.cursor;
              onChange(ergebnis.wert);
              bankPruefen(ergebnis.wert);
            }
          : undefined
      }
    />
  );
}

export default IbanEingabe;
